import { AntDesign } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router, useFocusEffect } from "expo-router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Text, View, TextInput, Pressable, ScrollView } from "react-native";
import { ActivityIndicator } from "react-native-paper";

import Logo from "@/assets/logo.svg";
import PasswordForgot from "@/assets/passwordrecovery.svg";
import Loader from "@/components/Loader";
import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import InputPassword from "@/components/input/InputPassword";
import { sendRequest } from "@/utils/ApiUtil";
import { ChangePassword } from "@/utils/dto/user.request";

type FormData = {
  code: string;
  password: string;
  passwordConfirmation: string;
};

export default function PasswordRecovery() {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isRecoveryFinished, setIsRecoveryFinished] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const resolver = classValidatorResolver(ChangePassword, {
    validator: { skipMissingProperties: true },
  });

  const { handleSubmit, formState, reset, control, setError } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  const { isSubmitting } = formState;

  useFocusEffect(
    React.useCallback(() => {
      return () => {
        reset();
        setIsCodeSent(false);
        setIsRecoveryFinished(false);
        setIsFetching(false);
      };
    }, [])
  );

  const fetchCode = async () => {
    setIsFetching(true);
    try {
      await sendRequest("get", "/password-reset", { email }, false);
      setIsCodeSent(true);
      setIsFetching(false);
    } catch (error: any) {
      console.log(error);
      setIsFetching(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (isRecoveryFinished) {
      router.replace("/");
      return;
    }

    if (data.password !== data.passwordConfirmation) {
      setError("passwordConfirmation", {
        type: "manual",
        message: t("passwordrecovery:passwordmatch"),
      });
      return;
    }

    try {
      await sendRequest("post", "/password-reset", data, false);
      setIsRecoveryFinished(true);
    } catch (error: any) {
      console.log(error);
    }
  };

  return (
    <ScrollView
      style={{
        backgroundColor: "#FFFFFF",
        padding: 8,
        flexGrow: 1,
      }}
    >
      <Pressable
        className="z-50 flex-row absolute top-4 left-4 items-center"
        onPress={() => {
          router.replace("/");
        }}
      >
        <AntDesign name="arrowleft" size={18} color="#919191" />
        <Text className="text-darkergray mx-2">
          {t("common:terms.backtologin")}
        </Text>
      </Pressable>
      <View className="h-full items-center justify-center">
        <Logo className="mb-4 mt-4" />
        <View className="mb-2">
          <Text className="text-blue1 text-4xl text-center font-medium">
            {t("passwordrecovery:title")}
          </Text>
        </View>
        <Text className="text-midgray text-base text-center">
          {!isCodeSent
            ? t("passwordrecovery:subtitle")
            : t("passwordrecovery:codesent")}
        </Text>
        <View className="flex-grow items-center justify-start py-2">
          <PasswordForgot width={180} height={180} />
          {!isCodeSent ? (
            <TextInput
              className="bg-white1 border-2 border-lightgray rounded text-4xl text-center font-light py-4 px-4"
              placeholder={t("passwordrecovery:placeholder")}
              onChangeText={(text) => setEmail(text)}
              placeholderTextColor="#DFDFDF"
            />
          ) : isCodeSent && !isRecoveryFinished ? (
            <View className="w-full items-center">
              <Input
                name="code"
                label={t("passwordrecovery:code")}
                control={control}
              />
              <InputPassword
                name="password"
                label={t("passwordrecovery:password")}
                control={control}
              />
              <Input
                name="passwordConfirmation"
                label={t("passwordrecovery:passwordconfirm")}
                control={control}
                isPassword
              />
            </View>
          ) : (
            <View className="w-full items-center">
              <AntDesign name="checkcircle" size={40} color="green" />
              <Text className="text-midgray text-lg text-center my-3">
                {t("passwordrecovery:passwordrecovered")}
              </Text>
            </View>
          )}
          <View className="mt-4 items-center">
            {isFetching ? (
              <View className="w-full items-center mt-4">
                <ActivityIndicator size="large" color="#5D0CBA" />
              </View>
            ) : !isCodeSent ? (
              <Button
                label={t("common:button.confirm")}
                onClick={() => fetchCode()}
              />
            ) : (
              <Button
                label={
                  !isRecoveryFinished
                    ? t("common:button.confirm")
                    : t("common:button.backtologin")
                }
                onClick={handleSubmit(onSubmit)}
              />
            )}
          </View>
        </View>
      </View>
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
